import { useFeatureIsOn, useFeatureValue } from "@growthbook/growthbook-react";
import config from "config/config";
import Content, { ContentNS } from "content/content";
import { useAppStore } from "store/store";

export interface OutageSpec {
  locale: string;
  title: string;
  description: string;
  buttonTitle?: string;
  redirectUrl?: string;
}

/*
Sample outage spec:
[
  {
    "locale": "en-US",
    "title": "[en] Service is temporary unavailable",
    "description": "[en] This is description"
  },
  {
    "locale": "es-MX",
    "title": "[es] Service is temporary unavailable",
    "description": "[es] This is description"
  }
]
*/
export class OutageInfo {
  isOutage: boolean;
  private specs: { [locale: string]: OutageSpec };
  private defaultRedirectUrl: string | undefined;

  constructor(
    isOutage: boolean,
    defaultRedirectUrl?: string | null,
    growthBookValue?: string
  ) {
    this.isOutage = isOutage;
    this.specs = {};
    this.defaultRedirectUrl =
      defaultRedirectUrl !== null ? defaultRedirectUrl : undefined;

    if (
      growthBookValue !== undefined &&
      growthBookValue.trim() !== "" &&
      growthBookValue.trim() !== "[]"
    ) {
      const parsed = JSON.parse(growthBookValue) as OutageSpec[];
      parsed.forEach((spec) => (this.specs[spec.locale] = spec));
    }
  }

  getTitle(locale: string): string {
    if (this.specs[locale] !== undefined) {
      return this.specs[locale].title;
    }
    return Content.shared.t("outage.title", { ns: ContentNS.ui });
  }

  getDescription(locale: string): string {
    if (this.specs[locale] !== undefined) {
      return this.specs[locale].description;
    }
    return Content.shared.t("outage.description", {
      ns: ContentNS.ui,
    });
  }

  getButtonTitle(locale: string): string | undefined {
    if (this.specs[locale] !== undefined) {
      return this.specs[locale].buttonTitle;
    }
    return Content.shared.t("outage.button_title", {
      ns: ContentNS.ui,
    });
  }

  getRedirectUrl(locale: string): string | undefined {
    if (this.specs[locale] !== undefined) {
      return this.specs[locale].redirectUrl;
    }
    return this.defaultRedirectUrl;
  }
}

const useOutageInfo = (): OutageInfo => {
  const isOutage = useFeatureIsOn(`${config.growthbook.tag}-outage`);
  const outageSpec = useFeatureValue(
    `${config.growthbook.tag}-outage-spec`,
    "[]"
  ); // fallback to empty array

  const defaultRedirectUrl = useAppStore((state) => state.returnUrl);

  return new OutageInfo(isOutage, defaultRedirectUrl, outageSpec);
};

export default useOutageInfo;
